import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { Locale, DateRange } from "@/shared/utils";
import { getUniqueSigningStatuses } from "@/features/legal/shared/utils";
import { useGetCombinedContractsListQuery } from "@/features/hr/contracts/api";
import type { ContractFillDraftListItem } from "@/features/hr/contracts/types";
import { usePagination, type UsePaginationReturn } from "@/shared/hooks";
import { useContractsFilter, hasActiveFilters } from "./useContractsFilter";
import { useContractsSort, toggleSort, type SortKey } from "./useContractsSort";
import { mapCombinedToGrouped, type GroupedContract } from "./useGroupedContracts";

export interface UseContractsListPageReturn {
  fillDrafts: ContractFillDraftListItem[];
  isLoading: boolean;
  isError: boolean;
  refetchContracts: ReturnType<typeof useGetCombinedContractsListQuery>["refetch"];
  search: string;
  setSearch: (value: string) => void;
  startDateRange: DateRange;
  setStartDateRange: (value: DateRange) => void;
  statusFilter: string | null;
  setStatusFilter: (value: string | null) => void;
  statusOptions: ReturnType<typeof getUniqueSigningStatuses>;
  activeFilters: boolean;
  view: "table" | "cards";
  setView: (value: "table" | "cards") => void;
  locale: Locale;
  sortConfig: { key: SortKey; direction: "asc" | "desc" } | null;
  handleSort: (key: SortKey) => void;
  filteredContracts: ReturnType<typeof useContractsFilter>;
  pagination: UsePaginationReturn;
  pageContracts: GroupedContract[];
  handleResetFilters: () => void;
  t: ReturnType<typeof useTranslation>["t"];
}

export function useContractsListPage(): UseContractsListPageReturn {
  const { i18n, t } = useTranslation("ContractsPage");
  const { t: tCommon } = useTranslation("Common");
  const locale = i18n.language as Locale;

  const { data, isLoading, isError, refetch: refetchContracts } = useGetCombinedContractsListQuery();
  const contracts = data?.contracts;
  const fillDrafts = data?.fill_drafts ?? [];

  const [search, setSearch] = useState("");
  const [startDateRange, setStartDateRange] = useState<DateRange>({
    start: null,
    end: null,
  });
  const [statusFilter, setStatusFilter] = useState<string | null>(null);

  const [view, setView] = useState<"table" | "cards">("cards");

  const [sortConfig, setSortConfig] = useState<{ key: SortKey; direction: "asc" | "desc" } | null>(null);

  function handleSort(key: SortKey) {
    setSortConfig((prev) => toggleSort(prev, key));
  }

  const statusOptions = useMemo(() => {
    if (!contracts) return [];
    return [{ label: t("filters.allStatuses"), value: null }, ...getUniqueSigningStatuses(contracts, tCommon)];
  }, [contracts, tCommon, t]);

  const filterState = useMemo(
    () => ({
      search,
      startDateRange,
      statusFilter,
    }),
    [search, startDateRange, statusFilter],
  );

  const filteredContracts = useContractsFilter(contracts, filterState);
  const sortedContracts = useContractsSort(filteredContracts, sortConfig);
  const groupedContracts = mapCombinedToGrouped(sortedContracts);

  const activeFilters = hasActiveFilters(filterState);

  const pagination = usePagination(filteredContracts.length, 10, [
    search,
    startDateRange,
    statusFilter,
  ]);

  const pageContracts = groupedContracts.slice(pagination.startIndex, pagination.endIndex);

  function handleResetFilters() {
    setSearch("");
    setStartDateRange({ start: null, end: null });
    setStatusFilter(null);
    pagination.setPage(1);
  }

  return {
    fillDrafts,
    isLoading,
    isError,
    refetchContracts,
    search,
    setSearch,
    startDateRange,
    setStartDateRange,
    statusFilter,
    setStatusFilter,
    statusOptions,
    activeFilters,
    view,
    setView,
    locale,
    sortConfig,
    handleSort,
    filteredContracts,
    pagination,
    pageContracts,
    handleResetFilters,
    t,
  };
}
